import { useEffect, useRef, useState } from 'react';
import { Button, Container, Group, Text, Title } from '@mantine/core';
import { Link, useSearchParams } from 'react-router-dom';
import { api, errorMessage } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { ErrorNote } from '../components/ErrorNote';
import { Loading } from '../components/Loading';

type Stage = 'checking' | 'confirmed' | 'failed';

/** Opened from the link in the confirmation email. It confirms on load, there is nothing to fill in. */
export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { status, refresh } = useAuth();
  const [stage, setStage] = useState<Stage>(token === '' ? 'failed' : 'checking');
  const started = useRef(false);

  useEffect(() => {
    if (token === '' || started.current) return;
    started.current = true;
    let cancelled = false;
    api
      .confirmEmailVerification(token)
      .then(() => {
        if (cancelled) return;
        setStage('confirmed');
        void refresh();
      })
      .catch(() => {
        if (!cancelled) setStage('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [refresh, token]);

  if (stage === 'checking') {
    return (
      <Container component="main" size={460} pt={80} pb={64}>
        <Title order={1}>Confirming your email address</Title>
        <Loading label="One moment" />
      </Container>
    );
  }

  if (stage === 'confirmed') {
    return (
      <Container component="main" size={460} pt={80} pb={64}>
        <Title order={1}>Email address confirmed</Title>
        <Text size="lg" mt="sm">
          Thank you, that is all we needed.
        </Text>
        <Group mt="lg">
          <Button component={Link} to={status === 'signed_in' ? '/interactions' : '/login'}>
            {status === 'signed_in' ? 'Back to your interactions' : 'Sign in'}
          </Button>
        </Group>
      </Container>
    );
  }

  return (
    <Container component="main" size={460} pt={80} pb={64}>
      <Title order={1}>That link did not work</Title>
      <Text size="lg" mt="sm" mb="lg">
        It may have expired, or the address may already be confirmed. Either way, a new email will
        sort it out.
      </Text>
      {status === 'signed_in' ? (
        <ResendButton />
      ) : (
        <Group>
          <Button component={Link} to="/login">
            Sign in to send a new email
          </Button>
        </Group>
      )}
    </Container>
  );
}

/** Asks for another confirmation email. Only shown to someone already signed in. */
function ResendButton() {
  const [sent, setSent] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function send() {
    setProblem(null);
    setBusy(true);
    try {
      await api.requestEmailVerification();
      setSent(true);
    } catch (error) {
      setProblem(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  if (sent)
    return (
      <Text c="violet" size="sm">
        Sent. Check your inbox for the new link.
      </Text>
    );

  return (
    <>
      {problem !== null && <ErrorNote message={problem} />}
      <Group>
        <Button disabled={busy} onClick={() => void send()}>
          {busy ? 'Sending...' : 'Send a new email'}
        </Button>
      </Group>
    </>
  );
}
